import { useRef, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useApi } from "../hooks/useApi";
import { Pagination } from "../components/ui/Pagination";
import { useDownloadWithToken } from "../hooks/downloadApi";
export default function CostCodesList() {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [requestError, setRequestError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const initialized = useRef(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRows, setTotalRows] = useState(0);
  const limit = 20; // per page
  const { apiCall } = useApi();
  const { downloadWithToken } = useDownloadWithToken();
  const [isFileDownloading, setIsFileDownloading] = useState(false);
  const [isCostCodesProcessing, setIsCostCodesProcessing] = useState(false);
  const [costCodesRequestError, setCostCodesRequestError] = useState(null);
  useEffect(() => {
    if (!initialized.current) {
      initialized.current = true;
      fetchPage(currentPage);
    }
  }, []);
  const fetchPage = async (page) => {
    setLoading(true);
    try {
      const cost_codes = await apiCall(`costcodes-fetch_cost_codes`, { params: { page: page, limit: limit } });
      const costcodesJSON = await cost_codes.json();
      setCodes(costcodesJSON.items || []);
      setTotalPages(costcodesJSON.totalPages);
      setTotalRows(costcodesJSON.totalRows); // from backend
      setLoading(false);
    }
    catch (error) {
      setRequestError(error.message);
    }

  };
  const createCostCodesRequest = async () => {
    if (!window.confirm("Are you sure you want to create a cost codes processing request? This will remove all the cost codes and the budget lines and import new ones.")) {
      return;
    }
    setCostCodesRequestError(null);
    setIsCostCodesProcessing(true);
    try {
      const result = await apiCall(`costcodes-create_cost_code_request`);
      const resultJson = await result.json();
      if (resultJson && resultJson.success) {
        setIsCostCodesProcessing(false);
      }
      if (resultJson && resultJson.error) {
        throw new Error(resultJson.error);
      }
    }
    catch (error) {
      setCostCodesRequestError(error.message);
      setIsCostCodesProcessing(false);
    }

  };
  const handleExport = async () => {
    let filename = 'cost-codes.csv';
    if (isFileDownloading) return;
    setIsFileDownloading(true);
    try {
      // 1. Fetch the data from your PHP backend
      const response = await apiCall(`costcodes-download_cost_codes_csv`);


      if (!response.ok) throw new Error('Network response was not ok');

      const reader = response.body.getReader();

      // 2. Create a stream to handle incoming chunks
      const stream = new ReadableStream({
        async start(controller) {
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            controller.enqueue(value);
          }
          controller.close();
        }
      });

      // 3. Convert the stream to a Blob URL
      const streamResponse = new Response(stream);
      const blob = await streamResponse.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      // 4. Trigger the download automatically
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();

      // 5. Clean up memory
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
      setIsFileDownloading(false);
    } catch (error) {
      console.error('Streaming download failed:', error);
      setIsFileDownloading(false);
    }
  };

  const goToPage = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
    fetchPage(page);
  };

  const highlightText = (text, query) => {
    if (!query) return text;
    const regex = new RegExp(`(${query})`, "gi");
    return text.replace(regex, '<span class="bg-yellow-300">$1</span>');
  };

  if (loading) return <p>Loading codes...</p>;
  if (requestError) return <p>Error: {requestError}</p>;


  const filteredCodes = codes.filter((code) =>
    code.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
    code.code_nospace.toString().includes(searchTerm)
  );

  return (
    <div className="p-6">
      <div className="flex justify-between mb-10">
        <h1 className="text-2xl font-bold">Codes List</h1>
        <div className="flex flex-col gap-4">
          <Link to="/add-code" className="btn btn-primary text-center">
            Add New Code
          </Link>
          <Link to="/add-budget-lines" className="btn btn-primary">
            Add Budget Lines
          </Link>
        </div>
      </div>
      {/* <div>
        <input
          type="text"
          placeholder="Search by description or code..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="border p-2 rounded w-full mb-4"
        />

      </div> */}
      <div className="flex justify-between">


        <button
          onClick={handleExport}
          disabled={isFileDownloading}
          className="mb-4"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            padding: '10px 20px',
            backgroundColor: isFileDownloading ? '#ccc' : '#4CAF50',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: isFileDownloading ? 'not-allowed' : 'pointer',
            fontSize: '14px'
          }}
        >
          {isFileDownloading ? 'Downloading...' : 'Download Cost Codes'}
        </button>
        <button
          onClick={createCostCodesRequest}
          disabled={isCostCodesProcessing}
          className="mb-4 inline-flex py-3 px-4 text-white border-radius-2 bg-amber-600"
        >
          {isCostCodesProcessing ? 'Processing...' : 'Create Cost Codes Processing Request'}
        </button>

      </div>
      <div className="text-right mb-4">
        {costCodesRequestError && <p className="text-red-500">Error: {costCodesRequestError}</p>}
      </div>
      <table className="w-full border-collapse border border-gray-300">
        <thead>
          <tr className="bg-gray-100">
            <th className="border p-2 w-[100px]">DBID</th>
            <th className="border p-2 w-[80px]">Project#</th>
            <th className="border p-2 w-[160px]">Code</th>
            <th className="border p-2">Description</th>
            <th className="border p-2 w-[30px]">Cost Type</th>
            <th className="border p-2 w-[50px]">Phase</th>
            <th className="border p-2 w-[50px]">Location</th>
          </tr>
        </thead>
        <tbody>
          {filteredCodes.map((code) => (
            <tr key={code.id} className="border">
              <td className="border p-2">
                {code.dbid}
              </td>
              <td className="border p-2">
                {code.projectNumber}
              </td>
              <td className="border p-2">
                <div className="flex items-center justify-between">
                  {highlightText(code.code, searchTerm)}
                  {/* <Link to={`/edit-code/${code.id}`} className="text-blue-600 hover:underline" dangerouslySetInnerHTML={{ __html: highlightText(code.code, searchTerm) }}>
                  </Link>
                  <Link to={`/edit-code/${code.id}`} className="text-blue-600 hover:underline">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-6">
                      <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L6.832 19.82a4.5 4.5 0 0 1-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 0 1 1.13-1.897L16.863 4.487Zm0 0L19.5 7.125" />
                    </svg>
                  </Link> */}
                </div>
              </td>
              <td className="border p-2" dangerouslySetInnerHTML={{ __html: highlightText(code.description, searchTerm) }}>
              </td>
              <td className="border p-2">
                {code.costType}
              </td>
              <td className="border p-2">
                {code.phase}
              </td>
              <td className="border p-2">
                {code.location}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={goToPage}
        loading={loading}
      />
    </div>
  );
}
